/**
 * Shared presentational base for the six virtualised div-grids on the
 * frontend (Trades, MTM, Inventory, Availability, RecallReport,
 * AvailabilityReport). LR-384.
 *
 * Owns:
 *   - the overflow-auto scroll container,
 *   - the @tanstack/react-virtual wiring,
 *   - the sticky header chrome (GRID_HEADER_CHROME, see LR-383),
 *   - the absolute-positioned virtualised body container.
 *
 * Deliberately OUT of scope (each grid layers these on top via header /
 * renderRow nodes): row selection, column pinning, drag-and-drop reorder,
 * inline inputs, context menus, sort buttons, per-row state colours.
 *
 * Migration contract: each existing grid must remain pixel-identical and
 * behaviour-identical after switching to this base. Per LR-383, the chrome
 * tokens live in `grid-style.ts`; this component does not introduce any new
 * visual decisions on its own.
 */
import React, { useRef } from 'react';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { clsx } from 'clsx';

import { GRID_HEADER_CHROME } from '@/components/shared/grid-style';
import { Z_INDEX } from '@/utils/z-index';

export interface VirtualizedGridRowRenderArgs<T> {
  /** The row payload at this index. */
  row: T;
  /** Row index into the `rows` array. */
  index: number;
  /**
   * The tanstack-virtual VirtualItem for this row. Undefined only during the
   * initial-paint fallback (see `initialPaintFallback` prop).
   */
  virtualRow: VirtualItem | undefined;
  /**
   * The CSS the row's outermost element MUST adopt verbatim — already encodes
   * the absolute positioning and per-row height the virtualizer demands.
   * Empty object during initial-paint fallback (parent supplies its own
   * static layout in that mode).
   */
  style: React.CSSProperties;
  /**
   * True while rendering the initial-paint fallback (virtualizer has not yet
   * produced any items but `rows.length > 0`). False in the steady state.
   */
  isInitialFallback: boolean;
}

export interface VirtualizedGridProps<T> {
  /** Row data. Order is taken as-is — sort/filter outside the grid. */
  rows: readonly T[];

  /**
   * Estimated px height of a single row. A plain number applies to all rows;
   * a function (mirroring useVirtualizer's `estimateSize`) lets callers vary
   * height by index — e.g. TradesTable's 32px `section-header` rows vs
   * 39px data rows.
   */
  estimateRowHeight: number | ((index: number) => number);

  /** tanstack-virtual overscan. Default: 5. */
  overscan?: number;

  /** Stable per-row key. Default: index. */
  getRowKey?: (row: T, index: number) => React.Key;

  /**
   * Header contents. The base wraps them in a div carrying
   * `GRID_HEADER_CHROME` (sticky positioning + colour/typography tokens). Pass
   * height-style / `shrink-0` / padding via `headerClassName` & `headerStyle`.
   */
  header: React.ReactNode;

  /** Tailwind classes appended to GRID_HEADER_CHROME on the header wrapper. */
  headerClassName?: string;

  /** Inline style for the header wrapper (e.g. GRID_HEADER_HEIGHT_STYLE). */
  headerStyle?: React.CSSProperties;

  /**
   * Renderer for a single body row. Must apply `style` to the outermost
   * element verbatim (it carries the virtualizer's absolute position) and
   * use `virtualRow.key` (or its own stable id) as the React key.
   */
  renderRow: (args: VirtualizedGridRowRenderArgs<T>) => React.ReactNode;

  /**
   * Ref to the scroll container (callers read scrollTop / attach listeners /
   * hand the element to downstream hooks). If omitted, the base owns it.
   */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;

  /**
   * Extra attributes attached to the scroll container (data-*, aria-*,
   * onScroll handlers, etc.). Cannot override the base's className / style /
   * ref / children.
   */
  scrollContainerProps?: Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'className' | 'style' | 'ref' | 'children'
  >;

  /**
   * Add `scrollbarGutter: stable` to the scroll container. Default: true
   * (matches Trades/MTM/Inventory/Availability). Pass `false` for Report
   * grids that historically did NOT reserve the gutter.
   */
  scrollbarGutterStable?: boolean;

  /**
   * If provided, REPLACES the virtualized body (e.g. a loading skeleton,
   * empty state, or error block). The header keeps rendering above it. Pass
   * `null` / `undefined` for the steady-state virtualized render.
   */
  bodyOverride?: React.ReactNode;

  /**
   * Extra inline style merged into the virtualised body wrapper (the
   * positioning context for absolute rows). Use for e.g. extra paddingBottom
   * when a sticky action bar overlaps the last row. The base always sets
   * `position: 'relative'`, `width: '100%'` and the dynamic `height`; values
   * passed here can override only `height`/`width`/`position` if explicitly
   * supplied (last-write-wins on plain object merge).
   */
  bodyStyle?: React.CSSProperties;

  /**
   * When `rows.length > 0` but the virtualizer hasn't yet measured the
   * scroll element (e.g. initial paint inside a flex-1 container), render
   * the first N rows un-positioned so the table doesn't flash empty. The
   * renderer receives `virtualRow: undefined`, `style: {}`, and
   * `isInitialFallback: true` — handle accordingly. Default: disabled.
   */
  initialPaintFallback?: number;

  /**
   * Optional sticky bottom totals bar (LR-469). Rendered as a `sticky bottom-0`
   * sibling AFTER the body, INSIDE the `minWidth: max-content` content wrapper,
   * so it pins to the viewport bottom on vertical scroll AND scrolls in lockstep
   * with the columns on horizontal scroll (column totals stay under their
   * columns for free). The node owns its own background / borders / pinned-zone
   * layout — the base only supplies the sticky positioning and the
   * {@link Z_INDEX.TABLE_STICKY_FOOTER} stacking. Pass `null`/`undefined`
   * (the default) for the five grids without a totals row — they are unchanged.
   */
  footer?: React.ReactNode;

  /** Tailwind classes appended to the sticky footer wrapper. */
  footerClassName?: string;

  /** Inline style for the sticky footer wrapper (can override the default zIndex). */
  footerStyle?: React.CSSProperties;
}

export function VirtualizedGrid<T>({
  rows,
  estimateRowHeight,
  overscan = 5,
  getRowKey,
  header,
  headerClassName,
  headerStyle,
  renderRow,
  scrollContainerRef,
  scrollContainerProps,
  scrollbarGutterStable = true,
  bodyOverride,
  bodyStyle,
  initialPaintFallback,
  footer,
  footerClassName,
  footerStyle,
}: VirtualizedGridProps<T>): React.ReactElement {
  const internalRef = useRef<HTMLDivElement>(null);
  const parentRef = scrollContainerRef ?? internalRef;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize:
      typeof estimateRowHeight === 'function'
        ? estimateRowHeight
        : () => estimateRowHeight,
    overscan,
    ...(getRowKey
      ? { getItemKey: (index: number) => getRowKey(rows[index], index) }
      : {}),
  });

  const virtualItems = virtualizer.getVirtualItems();
  const useFallback =
    bodyOverride == null &&
    virtualItems.length === 0 &&
    rows.length > 0 &&
    initialPaintFallback != null &&
    initialPaintFallback > 0;

  return (
    <div
      ref={parentRef as React.RefObject<HTMLDivElement>}
      {...scrollContainerProps}
      className="flex flex-1 flex-col overflow-auto"
      style={scrollbarGutterStable ? { scrollbarGutter: 'stable' } : undefined}
    >
      <div className="flex flex-1 flex-col" style={{ minWidth: 'max-content' }}>
        <div className={clsx(GRID_HEADER_CHROME, headerClassName)} style={headerStyle}>
          {header}
        </div>
        <div className="flex-1">
          {bodyOverride != null ? (
            bodyOverride
          ) : useFallback ? (
            <div>
              {rows
                .slice(0, Math.min(initialPaintFallback!, rows.length))
                .map((row, index) =>
                  renderRow({
                    row,
                    index,
                    virtualRow: undefined,
                    style: {},
                    isInitialFallback: true,
                  }),
                )}
            </div>
          ) : (
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
                ...bodyStyle,
              }}
            >
              {virtualItems.map((virtualRow) => {
                const row = rows[virtualRow.index];
                if (row == null) return null;
                const style: React.CSSProperties = {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                };
                return renderRow({
                  row,
                  index: virtualRow.index,
                  virtualRow,
                  style,
                  isInitialFallback: false,
                });
              })}
            </div>
          )}
        </div>
        {footer != null && (
          <div
            className={clsx('sticky bottom-0', footerClassName)}
            style={{ zIndex: Z_INDEX.TABLE_STICKY_FOOTER, ...footerStyle }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
